import {
	AfterContentChecked,
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { FileErrorMessages } from 'app/components/merchant/partners-portal/enums/file-error-messages.enum';
import {
	BYTES,
	DECIMAL,
	FIVE_MEGABYTES,
	FRACTION_DIGITS,
	VALID_FILE_TYPES,
} from 'app/components/merchant/partners-portal/validators/file-upload.validator';
import { ProductService } from 'app/services';
import { MessageService } from 'primeng/api';

export interface ProductTagModel {
	Id: number;
	Name: string;
	IsActive: boolean;
}

@Component({
	selector: 'product-detail-form',
	templateUrl: './product-detail-form.component.html',
	styleUrls: ['./product-detail-form.component.scss'],
	providers: [MessageService],
})
export class ProductDetailFormComponent
	implements OnInit, AfterViewInit, AfterContentChecked, OnChanges
{
	// Product Detail form
	@Input() productDetailFormGroup!: FormGroup;
	@Input() selectedTagList = new Set<ProductTagModel>();
	@Input() store: any;

	productImageToUpload!: File;
	productImageToUploadLocalUrl!: string;
	isProductImageMouseHover = false;
	isProductImageReuploadMouseHover = false;
	productCategory: any[] = [];
	tags: ProductTagModel[] = [];
	productCategories: any[] = [];
	subProductCategories: any[] = [];
	selectedProductCategory!: any;
	selectedProductSubCategory!: any;
	selectedTags!: ProductTagModel;
	filteredCategories: any[] = [];

	fileErrorMessage: string = '';

	@ViewChild('uploadContainer') myDiv!: ElementRef;

	constructor(
		private productService: ProductService,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		this.getAllCategories();
		this.getTags();
		this.initializeCategoryFilter();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['store'] && changes['store'].currentValue) {
			this.getTags();
			this.getAllCategories();
			this.productDetailFormGroup.get('Subcategory')?.disable();
		}
	}

	ngAfterViewInit(): void {
		this.productDetailFormGroup.valueChanges.subscribe((data) => {});
	}

	ngAfterContentChecked(): void {}

	fileValidator(file: File): boolean {
		const filesize = parseInt(
			(file.size / BYTES / BYTES).toFixed(FRACTION_DIGITS),
			DECIMAL
		);

		const fileTypeIsValid = VALID_FILE_TYPES.includes(file.type);
		if (!fileTypeIsValid) {
			this.fileErrorMessage = FileErrorMessages.INVALID_FILE_FORMAT;
			return false;
		}

		if (filesize > FIVE_MEGABYTES) {
			this.fileErrorMessage = FileErrorMessages.IMAGE_SIZE_EXCEEDED;
			return false;
		}

		return true;
	}

	onFileSelected(event: any) {
		const selectedFile = event.target.files;

		if (!selectedFile || selectedFile.length === 0) {
			return;
		}

		const fileIsValid = this.fileValidator(selectedFile[0] as File);

		if (!fileIsValid) {
			this.productDetailFormGroup.get('ImageUrl')?.setValue('');
			return;
		}

		this.fileErrorMessage = '';
		this.productImageToUpload = selectedFile[0];
		const reader = new FileReader();
		reader.readAsDataURL(this.productImageToUpload);
		reader.onload = () => {
			this.productImageToUploadLocalUrl = `${reader.result?.toString()}`;
			this.productDetailFormGroup
				.get('ImageUrl')
				?.setValue(this.productImageToUploadLocalUrl);
		};
	}

	onProductImageMouseEntered() {
		this.isProductImageMouseHover = true;
	}

	onProductImageMouseLeave(event: any) {
		if (this.myDiv) {
			const rect = this.myDiv.nativeElement.getBoundingClientRect();
			const mouseX = event.clientX;
			const mouseY = event.clientY;

			const isOutside = !(
				mouseX >= rect.left &&
				mouseX <= rect.right &&
				mouseY >= rect.top &&
				mouseY <= rect.bottom
			);
			if (isOutside) {
				this.isProductImageMouseHover = false;
			} else {
				this.isProductImageMouseHover = true;
			}
		}
	}

	onProductReuploadMouseEntered() {
		this.isProductImageReuploadMouseHover = true;
	}

	private initializeCategoryFilter(): void {
		this.filteredCategories = [...this.productCategories];
	}

	displayCategoryFn(category: any): string {
		return category && category.Name ? category.Name : '';
	}

	onCategorySelected(event: any): void {
		this.selectedProductCategory = event.option.value;
		this.onSelectionChangeSubCategory();
	}

	filterCategories(value: string): void {
		if (!value) {
			this.filteredCategories = [...this.productCategories];
		} else {
			this.filteredCategories = this.productCategories.filter(category =>
				category.Name.toLowerCase().includes(value.toLowerCase())
			);
		}
	}

	private getTags(): void {
		this.productService.getTags(null, this.messageService).then((data) => {
			if (!data) {
				return;
			}

			if (!data.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: data.ErrorMessage,
				});
				return;
			}
			this.tags = data.Items;
		});
	}

	private getAllCategories(): void {
		const dto = {
			ParentId: null,
			GetAll: true,
			MerchantId: null,
		};
		this.productService.getCategories(dto, this.messageService).then((data) => {
			if (!data) {
				return;
			}
			this.productCategories = data;
			this.initializeCategoryFilter();
		});
	}

	private getSubCategory() {
		const dto = {
			ParentId: this.selectedProductCategory.Id,
			GetAll: true,
			MerchantId: null,
		};
		this.productService.getCategories(dto, this.messageService).then((data) => {
			if (!data) {
				return;
			}
			if (data.length === 0) {
				this.productDetailFormGroup.get('Subcategory')?.disable();
				this.productDetailFormGroup.get('Subcategory')?.clearValidators();
			} else {
				this.productDetailFormGroup.get('Subcategory')?.enable();
				this.productDetailFormGroup.get('Subcategory')?.setValidators([Validators.required]);
			}
			this.productDetailFormGroup.get('Subcategory')?.updateValueAndValidity();
			this.subProductCategories = data;
		});
	}

	onSelectionChangeSubCategory() {
		if (this.selectedProductCategory) {
			this.productDetailFormGroup.get('Subcategory')?.enable();
			this.productDetailFormGroup.get('Subcategory')?.setValue(null); // Reset subcategory when category changes
			this.getSubCategory();
		}
	}

	addTags(tags: ProductTagModel) {
		this.selectedTagList.add(tags);
		this.productDetailFormGroup.value.Tags = [
			...Array.from(this.selectedTagList),
		];
	}

	removeSelectedTags(tags: ProductTagModel) {
		this.selectedTagList.delete(tags);
		this.productDetailFormGroup.value.Tags = [
			...Array.from(this.selectedTagList),
		];
	}
}
